/**
 * Exposure Apparel — print placement catalogue + garment illustrations.
 *
 * Shared by the product page builder (custom-main-product.js) and the cart
 * "Add customisation" pop-up (ea-cart-customisations.js) so both offer the same
 * locations, sizes and drawings.
 *
 * Front view is drawn as you look at the wearer, so the wearer's LEFT chest,
 * side and sleeve appear on the viewer's RIGHT. Back view is not mirrored.
 */
(function () {
  'use strict';

  const LOGO_URL = 'https://cdn.shopify.com/s/files/1/0740/1871/7987/files/Untitled_design_5.png?v=1767844945';

  // Same catalogue as APPAREL_PRINT_LOCATIONS in custom-main-product.js
  const AREAS = [
    {
      id: 'Front',
      view: 'front',
      spots: [
        { value: 'Front Left Chest', label: 'Left Chest' },
        { value: 'Center Front', label: 'Centre Chest' },
        { value: 'Full Front', label: 'Full Front' },
        { value: 'Front Right Chest', label: 'Right Chest', advanced: true },
        { value: 'Over Pocket', label: 'Over Pocket', advanced: true },
        { value: 'Lower Front', label: 'Lower Front', advanced: true },
        { value: 'Lower Left', label: 'Lower Left', advanced: true },
        { value: 'Lower Right', label: 'Lower Right', advanced: true },
        { value: 'Vertical Left', label: 'Vertical Left', advanced: true },
        { value: 'Vertical Right', label: 'Vertical Right', advanced: true },
      ],
    },
    {
      id: 'Back',
      view: 'back',
      spots: [
        { value: 'Top Back', label: 'Upper Back' },
        { value: 'Center Back', label: 'Full Back' },
        { value: 'Lower Back', label: 'Lower Back', advanced: true },
        { value: 'Vertical Back L', label: 'Vertical Left', advanced: true },
        { value: 'Vertical Back R', label: 'Vertical Right', advanced: true },
      ],
    },
    {
      id: 'Sleeves',
      view: 'front',
      spots: [
        { value: 'Left Sleeve', label: 'Left Sleeve' },
        { value: 'Right Sleeve', label: 'Right Sleeve' },
      ],
    },
    {
      id: 'Sides',
      view: 'front',
      spots: [
        { value: 'Left Side', label: 'Left Side' },
        { value: 'Right Side', label: 'Right Side' },
      ],
    },
  ];

  // Print positions Tapstitch can actually print on each blank (from the blank's saved design in
  // the Tapstitch account, Oct 2026). Sleeves are only offered where Tapstitch lists left/right
  // sleeve; no blank lists side-seam prints, so "Sides" is never offered. Products not listed here
  // get Front + Back only. Add new products here when they're imported (and to ea-design-calibration.js).
  const TAPSTITCH_AREAS = {
    'boxy-fleece-crew': ["front", "back"],
    'boxy-fleece-hoodie': ["front", "back", "left_sleeve", "right_sleeve"],
    'classic-cotton-hoodie-300gsm': ["front", "back"],
    'classic-cotton-tee-180gsm': ["front", "back", "left_sleeve", "right_sleeve"],
    'contrast-piping-polo-220gsm': ["front", "back"],
    'cool-touch-boxy-tee-220gsm': ["front", "back"],
    'essential-cotton-tee-220gsm': ["front", "back", "left_sleeve", "right_sleeve"],
    'essential-fleece-joggers-320gsm': ["front", "back"],
    'essential-oversized-hoodie': ["front", "back"],
    'everyday-cotton-tee-230gsm': ["front", "back"],
    'extended-size-cotton-tee-260gsm': ["front", "back"],
    'full-zip-fleece-hoodie-350gsm': ["front", "back"],
    'heavyweight-cotton-tee-260gsm': ["front", "back", "left_sleeve", "right_sleeve"],
    'heavyweight-crew-sweatshirt-320gsm': ["front", "back"],
    'heavyweight-drop-shoulder-tee-300gsm': ["front", "back", "left_sleeve", "right_sleeve"],
    'heavyweight-earth-tone-tee-305gsm': ["front", "back", "left_sleeve", "right_sleeve"],
    'heavyweight-fleece-shorts-420gsm': ["front", "back"],
    'heavyweight-premium-hoodie-430gsm': ["front", "back", "left_sleeve", "right_sleeve"],
    'heavyweight-pullover-hoodie': ["front", "back"],
    'heavyweight-sweatpants': ["front", "back"],
    'heavyweight-zip-hoodie': ["front", "back"],
    'kids-cotton-tee-180gsm': ["front", "back"],
    'mineral-wash-boxy-tee-275gsm': ["front", "back", "left_sleeve", "right_sleeve"],
    'oversized-crew-sweatshirt-290gsm': ["front", "back"],
    'oversized-fleece-hoodie-350gsm': ["front", "back", "left_sleeve", "right_sleeve"],
    'oversized-long-sleeve-tee-305gsm': ["front", "back", "left_sleeve", "right_sleeve"],
    'performance-sports-tee-150gsm': ["front", "back"],
    'premium-heavyweight-crew-420gsm': ["front", "back"],
    'quick-dry-relaxed-tee-200gsm': ["front", "back"],
    'seamless-cotton-tee-200gsm': ["front", "back", "left_sleeve", "right_sleeve"],
    'stretch-crew-tee-180gsm': ["front", "back", "left_sleeve", "right_sleeve"],
    'structured-heavyweight-crew': ["front", "back"],
    'structured-heavyweight-hoodie': ["front", "back"],
    'structured-heavyweight-joggers': ["front", "back"],
    'sunfade-crew': ["front", "back"],
    'sunfade-hoodie': ["front", "back"],
    'sunfade-sweatpants': ["front", "back"],
    'ultra-heavyweight-cotton-crew': ["front", "back"],
    'ultra-heavyweight-cotton-hoodie': ["front", "back"],
    'ultra-heavyweight-cotton-sweatpants': ["front", "back"]
  };

  function allowedAreaIds(handle) {
    const a = TAPSTITCH_AREAS[handle] || ['front', 'back'];
    const ids = [];
    if (a.includes('front')) ids.push('Front');
    if (a.includes('back')) ids.push('Back');
    if (a.includes('left_sleeve') || a.includes('right_sleeve')) ids.push('Sleeves');
    if (a.includes('left_side') || a.includes('right_side')) ids.push('Sides');
    return ids;
  }

  function areasFor(handle) {
    const ids = allowedAreaIds(handle);
    return AREAS.filter((area) => ids.includes(area.id));
  }

  // Same rule as getLocationSize() in custom-main-product.js
  const LARGE = [
    'Full Front', 'Lower Front', 'Vertical Left', 'Vertical Right',
    'Top Back', 'Center Back', 'Lower Back', 'Vertical Back L', 'Vertical Back R',
  ];

  const spotIndex = {};
  AREAS.forEach((area) => area.spots.forEach((spot) => { spotIndex[spot.value] = { ...spot, area: area.id, view: area.view }; }));

  function getSize(value, method) {
    if (window.EA_PRINT_RULES) return window.EA_PRINT_RULES.locationSize(value, method);
    return LARGE.includes(value) ? 'Large' : 'Small';
  }

  function getSpot(value) {
    return spotIndex[value] || null;
  }

  // Placement areas in garment units (viewBox 0 0 120 150; body x 31–89, collar y≈20, hem y≈128)
  const centred = (offset, w) => 60 + offset - w / 2;
  const RECTS = {
    'Front Left Chest': { x: centred(11, 12), y: 35, width: 12, height: 10 },
    'Front Right Chest': { x: centred(-11, 12), y: 35, width: 12, height: 10 },
    'Center Front': { x: centred(0, 14), y: 33, width: 14, height: 10 },
    'Full Front': { x: centred(0, 34), y: 34, width: 34, height: 44 },
    'Over Pocket': { x: centred(0, 28), y: 96, width: 28, height: 16 },
    'Lower Front': { x: centred(0, 32), y: 104, width: 32, height: 16 },
    'Lower Left': { x: centred(16, 16), y: 104, width: 16, height: 16 },
    'Lower Right': { x: centred(-16, 16), y: 104, width: 16, height: 16 },
    'Vertical Left': { x: centred(18, 8), y: 36, width: 8, height: 76 },
    'Vertical Right': { x: centred(-18, 8), y: 36, width: 8, height: 76 },
    'Top Back': { x: centred(0, 36), y: 30, width: 36, height: 12 },
    'Center Back': { x: centred(0, 36), y: 33, width: 36, height: 50 },
    'Lower Back': { x: centred(0, 32), y: 104, width: 32, height: 16 },
    'Vertical Back L': { x: centred(-18, 8), y: 34, width: 8, height: 78 },
    'Vertical Back R': { x: centred(18, 8), y: 34, width: 8, height: 78 },
    // Sleeves follow the sleeve's 45° axis
    'Left Sleeve': { x: 95, y: 36.5, width: 9, height: 12, rot: -45 },
    'Right Sleeve': { x: 16, y: 36.5, width: 9, height: 12, rot: 45 },
    // Side seams: narrow strip from under the arm to the hem
    'Left Side': { x: 84, y: 54, width: 4.5, height: 66 },
    'Right Side': { x: 31.5, y: 54, width: 4.5, height: 66 },
  };

  // An SVG shown through <img src="data:..."> can't load external images, so the logo
  // must be embedded as a data URI. Until it is, the print area is drawn without it
  // (never a broken-image icon); 'ea:placement-logo-ready' fires once it's available.
  let logoHref = window._placementImageDataURI || null;
  if (!logoHref) {
    fetch(LOGO_URL, { mode: 'cors' })
      .then((res) => (res.ok ? res.blob() : Promise.reject(new Error('logo fetch failed'))))
      .then(
        (blob) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          })
      )
      .then((dataUri) => {
        logoHref = dataUri;
        if (!window._placementImageDataURI) window._placementImageDataURI = dataUri;
        document.dispatchEvent(new CustomEvent('ea:placement-logo-ready'));
      })
      .catch(() => {
        /* keep drawing print areas without the logo */
      });
  }

  function svg(location, view) {
    const spot = getSpot(location);
    const isBack = String(view || (spot && spot.view) || '').toLowerCase() === 'back';
    const ink = '#1f2937';
    const fabric = '#f9fafb';
    const accent = '#0eadb6';

    const neckline = isBack ? 'Q 60 25 74 20' : 'Q 60 33 74 20';
    const ribLine = isBack ? 'M 43 21 Q 60 29 77 21' : 'M 43 21 Q 60 38 77 21';
    const garment =
      `<path d="M 46 20 ${neckline} L 93 26 L 111 48 L 101 57 L 89 48 L 89 128 Q 60 130.5 31 128 L 31 48 L 19 57 L 9 48 L 27 26 Z" fill="${fabric}" stroke="${ink}" stroke-width="1.6" stroke-linejoin="round" stroke-linecap="round"/>` +
      `<path d="${ribLine}" fill="none" stroke="${ink}" stroke-width="1.1" stroke-linecap="round"/>` +
      `<path d="M 11 45.8 L 21 54.8 M 109 45.8 L 99 54.8" fill="none" stroke="${ink}" stroke-width="0.8" stroke-dasharray="1.6 1.2" opacity="0.55"/>` +
      `<path d="M 32 124.5 Q 60 127 88 124.5" fill="none" stroke="${ink}" stroke-width="0.8" stroke-dasharray="1.6 1.2" opacity="0.55"/>` +
      `<path d="M 31 48 L 31 52 M 89 48 L 89 52" fill="none" stroke="${ink}" stroke-width="0.8" opacity="0.4"/>`;

    const pocket = location === 'Over Pocket'
      ? `<path d="M 44 92 L 76 92 L 82 116 L 38 116 Z" fill="none" stroke="${ink}" stroke-width="0.9" stroke-dasharray="2 1.4" opacity="0.7"/>`
      : '';

    let area = '';
    const rect = RECTS[location];
    if (rect) {
      const cx = rect.x + rect.width / 2;
      const cy = rect.y + rect.height / 2;
      const pad = Math.min(rect.width, rect.height) > 6 ? 1.4 : 0.6;
      const vertical = rect.height > rect.width * 2.2;
      const innerW = (vertical ? rect.height : rect.width) - pad * 2;
      const innerH = (vertical ? rect.width : rect.height) - pad * 2;
      const href = logoHref || window._placementImageDataURI;
      const logo = href ? `<image href="${href}" x="${cx - innerW / 2}" y="${cy - innerH / 2}" width="${innerW}" height="${innerH}" preserveAspectRatio="xMidYMid meet"${vertical ? ` transform="rotate(-90 ${cx} ${cy})"` : ''}/>` : '';
      const box = `<rect x="${rect.x}" y="${rect.y}" width="${rect.width}" height="${rect.height}" rx="1.6" fill="${accent}" fill-opacity="0.16" stroke="${accent}" stroke-width="1" stroke-dasharray="2.2 1.3"/>`;
      area = rect.rot ? `<g transform="rotate(${rect.rot} ${cx} ${cy})">${box}${logo}</g>` : box + logo;
    }

    const markup = `<svg width="160" height="200" viewBox="0 0 120 150" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">${garment}${pocket}${area}</svg>`;
    return 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(markup)));
  }

  // Spots that physically overlap, so they can't both be printed. Shared by the
  // product-page picker (ea-builder-steps.js) and the cart pop-up (ea-cart-customisations.js).
  const CONFLICTS = {
    'Full Front': ['Front Left Chest', 'Front Right Chest', 'Center Front', 'Over Pocket', 'Lower Front', 'Lower Left', 'Lower Right'],
    'Center Back': ['Top Back', 'Lower Back'],
  };
  const conflictsWith = (a, b) => (CONFLICTS[a] || []).includes(b) || (CONFLICTS[b] || []).includes(a);

  // Cart lines can carry a spot's value ("Center Back"), its label ("Left Chest")
  // or a British spelling ("Centre Back"); map any of them to the spot value.
  function normaliseSpot(text) {
    const raw = String(text || '').trim();
    if (spotIndex[raw]) return raw;
    const key = (v) => v.toLowerCase().replace(/centre/g, 'center');
    const k = key(raw);
    const all = Object.values(spotIndex);
    const hit = all.find((sp) => key(sp.value) === k) || all.find((sp) => key(sp.label) === k);
    return hit ? hit.value : raw;
  }

  window.EAPrintPlacements = { AREAS, areasFor, allowedAreaIds, getSize, getSpot, svg, CONFLICTS, conflictsWith, normaliseSpot };
})();

